import React, { useEffect, useState } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";
import API_URL from "../../api/API";
import { RichTextEditor } from "../../components/RichTextEditor";
import { PreviewBody } from "../../components/PreviewBody";

export const AdminManageStateWaiverClauses = () => {
  const { admin } = useAdminAuthContext();

  const [clauses, setClauses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [stateName, setStateName] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [previewClause, setPreviewClause] = useState(null); // For preview

  // Fetch all clauses
  const fetchClauses = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URL.ADMIN_STATE_WAIVER_CLAUSES_GET, {
        headers: { Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
      });
      const data = await res.json();
      if (data.success) setClauses(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClauses();
  }, []);

  const handleCreate = () => {
    setShowForm(true);
    setEditingId(null);
    setStateName("");
    setBody("");
    setMessage("");
  };

  const handleSave = async () => {
    if (!stateName.trim() || !body.trim()) {
      alert("State and body are required.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(API_URL.ADMIN_STATE_WAIVER_CLAUSES_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname,
        },
        body: JSON.stringify({ state: stateName, body }),
      });
      const data = await res.json();

      if (data.success) {
        setMessage(data.message);
        setStateName("");
        setBody("");
        setEditingId(null);
        setShowForm(false);
        fetchClauses();
      } else {
        alert(data.message || "Error saving clause");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving clause");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (clause) => {
    setEditingId(clause._id);
    setStateName(clause.state);
    setBody(clause.body);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this clause?")) return;

    try {
      setLoading(true);
      const res = await fetch(API_URL.ADMIN_STATE_WAIVER_CLAUSES_DELETE + id, {
        method: "POST",
        headers: { Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
      });
      const data = await res.json();
      if (data.success) fetchClauses();
      else alert(data.message || "Failed to delete clause");
    } catch (err) {
      console.error(err);
      alert("Error deleting clause");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSideMenu />
      <div className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-2xl font-semibold mb-4">Manage State Waiver Clauses</h1>

        <button
          onClick={handleCreate}
          className="mb-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Create New Clause
        </button>

        {message && (
          <div className="mb-4 p-2 bg-blue-100 text-blue-700 rounded">{message}</div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">State Name</label>
              <input
                type="text"
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
                className="w-full border p-2 rounded"
                placeholder="Enter state name (e.g. California)"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Clause Body (use placeholders like [BUSINESS_NAME], [COUNTY])
              </label>
              <RichTextEditor
                key={editingId || "new"} // ensures proper prefill
                content={body}
                onChange={setBody}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {editingId ? "Update Clause" : "Save Clause"}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setStateName("");
                  setBody("");
                  setMessage("");
                }}
                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Clause List */}
        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">Existing Clauses</h2>
          {loading && <p>Loading...</p>}
          {!loading && clauses.length === 0 && <p className="text-gray-500">No clauses added yet.</p>}
          {!loading && clauses.length > 0 && (
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">State</th>
                  <th className="p-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clauses.map((c) => (
                  <tr key={c._id}>
                    <td className="p-2 border">{c.state}</td>
                    <td className="p-2 border text-center space-x-2">
                      <button
                        onClick={() => handleEdit(c)}
                        className="text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(c._id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setPreviewClause(c)}
                        className="text-green-600 hover:underline"
                      >
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {previewClause && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded shadow-lg w-3/4 max-h-[80vh] overflow-y-auto">
      <h3 className="text-xl font-semibold mb-4">
        {previewClause.state} Clause Preview
      </h3>

      <div
        className="max-w-full p-4 preview-body"
        dangerouslySetInnerHTML={{ __html: previewClause.body }}
      />

      <button
        onClick={() => setPreviewClause(null)}
        className="mt-4 bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
      >
        Close Preview
      </button>
    </div>
  </div>
)}

      </div>
    </div>
  );
};
